import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { getAppealTabs } from 'entities/tour/libs/getAppealsDetails'
import {
  type ClientDataFormValues,
  getClientDataAppealPayload,
} from 'entities/tour/libs/getClientDataAppealPayload'
import { selectionStore } from 'entities/tour/models'
import searchTour from 'entities/tour/models/search-tour/search-tour'
import tourList from 'entities/tour/models/tour-list/tour-list'
import { TourNavigation } from 'entities/tour/ui/appeal-details/components/tour-navigation'
import {
  MaxFiledLength,
  MinFiledLength,
} from 'entities/tour/ui/purchase-form-tours/model'
import userStore from 'entities/user/module/user-store'

import { BuyTourTicketApi } from 'shared/api/tours/buy-ticket'
import { TYPE_AGE } from 'shared/constants'
import { usePrivacyPolicyDocument } from 'shared/hooks'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText'
import { getRawColor } from 'shared/lib/getRawColor'
import { preparePhoneNumber } from 'shared/lib/prepare-phone-number'
import { priceShow } from 'shared/lib/priceShow'
import { Button } from 'shared/ui/button'
import { CheckBox } from 'shared/ui/check-box'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import {
  PhoneInputWhitLabel,
  customValidatePhone,
} from 'shared/ui/phone-input-label'
import { PopupContent } from 'shared/ui/poup-content'

import s from './styles.module.scss'

const KEYBOARD_MIN_HEIGHT = 150

const getViewportHeight = () =>
  window.visualViewport?.height ?? window.innerHeight

const ClientDataPage = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isAgreementChecked, setIsAgreementChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const expandedViewportHeight = useRef<number | null>(null)
  const isKeyboardOpen = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const formFieldsRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const hasSyncedAccount = useRef(false)
  const { closeDialog, openDialog } = useDialogBottomSheet()
  const { openPrivacyPolicy } = usePrivacyPolicyDocument()

  const hotelDetail = selectionStore.getSelectedHotel()
  const roomDetail = selectionStore.getSelectedRoom()
  const flight = selectionStore.getSelectedFlight()
  const hotelSearchSessionId = tourList.getUid()
  const passengers = searchTour.getPassengers()
  const departureCityId = searchTour.getDataForSearchTour()?.departure?.id
  const account = userStore.getAccountInfo()

  const methods = useForm<ClientDataFormValues>({
    defaultValues: {
      firstName: account?.first_name ?? '',
      lastName: account?.last_name ?? '',
      phoneNumber: preparePhoneNumber(account?.phone_number),
    },
  })

  useLayoutEffect(() => {
    const content = contentRef.current
    const footer = footerRef.current
    if (!content || !footer) return

    const updateFooterHeight = () => {
      const height = footer.getBoundingClientRect().height
      if (height > 0)
        content.style.setProperty('--footer-height', `${height}px`)
    }

    updateFooterHeight()
    const observer = new ResizeObserver(updateFooterHeight)
    observer.observe(footer)

    return () => observer.disconnect()
  }, [])

  const showFooter = useCallback(() => {
    expandedViewportHeight.current = null
    isKeyboardOpen.current = false
    setIsEditing(false)
  }, [])

  const restoreFooter = useCallback(() => {
    if (formFieldsRef.current?.contains(document.activeElement)) return

    const height = getViewportHeight()
    if (height >= (expandedViewportHeight.current ?? height) - 8) {
      showFooter()
    }
  }, [showFooter])

  // Android closes the keyboard (back / hide button) without blurring the input,
  // so the footer is restored by the viewport growing back, not by blur
  const handleViewportResize = useCallback(() => {
    const expandedHeight = expandedViewportHeight.current
    if (expandedHeight === null) return

    const height = getViewportHeight()
    if (height < expandedHeight - KEYBOARD_MIN_HEIGHT) {
      isKeyboardOpen.current = true
      return
    }
    if (height < expandedHeight - 8) return

    if (!isKeyboardOpen.current) {
      restoreFooter()
      return
    }

    const activeElement = document.activeElement
    if (
      activeElement instanceof HTMLElement &&
      formFieldsRef.current?.contains(activeElement)
    ) {
      // blur so the next tap on the same input fires focus and hides the footer again
      activeElement.blur()
    }
    showFooter()
  }, [restoreFooter, showFooter])

  useEffect(() => {
    if (!isEditing) return

    window.visualViewport?.addEventListener('resize', handleViewportResize)
    window.addEventListener('resize', handleViewportResize)

    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize)
      window.removeEventListener('resize', handleViewportResize)
    }
  }, [isEditing, handleViewportResize])

  useEffect(() => {
    if (!roomDetail || !hotelDetail) {
      navigate(RoutingPaths.tours.path())
    }
  }, [roomDetail, hotelDetail, navigate])

  useEffect(() => {
    if (account && !hasSyncedAccount.current) {
      methods.reset({
        firstName: account.first_name ?? '',
        lastName: account.last_name ?? '',
        phoneNumber: preparePhoneNumber(account?.phone_number),
      })
      hasSyncedAccount.current = true
    }
  }, [account, methods])

  const showErrorDialog = useCallback(
    (description: string) => {
      openDialog({
        isDialog: false,
        child: (
          <PopupContent
            image={ErrorImage}
            button={t('close')}
            title={t('error')}
            description={description}
            onClick={closeDialog}
          />
        ),
      })
    },
    [openDialog, closeDialog, t],
  )

  const onSubmit = async (formValues: ClientDataFormValues) => {
    const payload = getClientDataAppealPayload({
      formValues,
      flight,
      hotel: hotelDetail ?? null,
      room: roomDetail ?? null,
      hotelSearchSessionId,
      departureCityId,
    })

    if (!payload) {
      showErrorDialog(t('tours_block.tours_error_build_booking'))
      return
    }

    setIsLoading(true)
    try {
      const response = await BuyTourTicketApi.createTourAppeal(payload)
      const appealId = response.data.data.id
      await BuyTourTicketApi.confirmAppeal(appealId)
      navigate(RoutingPaths.tours.success.path())
    } catch (error) {
      const errorText = getErrorMessage(error)
      showErrorDialog(errorText || t('tours_block.tours_error_booking'))
    } finally {
      setIsLoading(false)
    }
  }

  const adultsCount = passengers?.adults ?? 1
  const childrenCount = passengers?.children ?? 0

  const appealTabsData = useMemo(
    () => ({
      hotel: {
        hotel_name: hotelDetail?.hotel_name,
        tours: [{ room_info: { name: roomDetail?.room } }],
        operator: hotelDetail?.operator ?? '',
      },
      personal_data: {
        [TYPE_AGE.ADULTS]: Array(adultsCount),
        [TYPE_AGE.CHILDREN]: Array(childrenCount),
      },
    }),
    [hotelDetail, roomDetail, adultsCount, childrenCount],
  )

  const handleMoreHotelDetails = useCallback(() => {
    navigate(RoutingPaths.tours.tourDetail.path(hotelDetail?.id ?? ''), {
      state: {
        title: t('tours_block.tour_details'),
        subtitle: hotelDetail?.hotel_name ?? '',
        hotel: hotelDetail
          ? {
              ...hotelDetail,
              tours: roomDetail ? [roomDetail] : [],
            }
          : undefined,
        isViewMode: true,
      },
    })
  }, [navigate, hotelDetail, roomDetail, t])

  const tabs = useMemo(
    () =>
      getAppealTabs({
        t,
        appealDetails: appealTabsData,
        handleMoreHotelDetails,
      }),
    [t, appealTabsData, handleMoreHotelDetails],
  )

  console.log(tabs, 'tabs')

  const totalPrice = roomDetail?.price != null ? String(roomDetail.price) : '0'

  if (!roomDetail || !hotelDetail) {
    return null
  }

  return (
    <div className={s.page}>
      <HeaderGoBack
        title={t('tours_block.client_data')}
        variant="white"
        arrowColor={getRawColor('--black')}
        buttonWhite
      />

      <div
        ref={contentRef}
        className={clsx(s.content, isEditing && s.contentEditing)}
      >
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className={s.form}
        >
          <Paper
            padding={[16]}
            radius={16}
            className={s.formPaper}
          >
            <p className={clsx('bold-16')}>{t('tours_block.contact_person')}</p>

            <div
              ref={formFieldsRef}
              className={s.formFields}
              onFocusCapture={() => {
                expandedViewportHeight.current ??= getViewportHeight()
                setIsEditing(true)
              }}
              onBlurCapture={() => window.requestAnimationFrame(restoreFooter)}
            >
              <Controller
                name="lastName"
                control={methods.control}
                rules={{
                  required: true,
                  minLength: MinFiledLength,
                  maxLength: MaxFiledLength,
                }}
                render={({ field }) => (
                  <InputWithLabel
                    ref={field.ref}
                    variant="secondary"
                    label={t('tours_block.client_data_last_name')}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={methods.formState.errors.lastName}
                  />
                )}
              />
              <Controller
                name="firstName"
                control={methods.control}
                rules={{
                  required: true,
                  minLength: MinFiledLength,
                  maxLength: MaxFiledLength,
                }}
                render={({ field }) => (
                  <InputWithLabel
                    ref={field.ref}
                    variant="secondary"
                    label={t('tours_block.client_data_first_name')}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    error={methods.formState.errors.firstName}
                  />
                )}
              />
              <Controller
                name="phoneNumber"
                control={methods.control}
                rules={{ validate: customValidatePhone }}
                render={({ field }) => (
                  <PhoneInputWhitLabel
                    variant="secondary"
                    label={t('purchase_page.phone_number')}
                    value={field.value}
                    error={methods.formState.errors.phoneNumber}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </Paper>

          <div className={s.infoSection}>
            <TourNavigation tabs={tabs} />
          </div>

          <div
            ref={footerRef}
            className={s.footer}
            hidden={isEditing}
          >
            <div className={s.header}>
              <div className={s.totalRow}>
                <span className="semi-bold-16">{t('total')}</span>
                <span className="extra-bold-18">
                  {priceShow(totalPrice)} &#8384;
                </span>
              </div>
            </div>

            <div className={s.divider} />

            <div className={s.agreement}>
              <div className={s.checkBox}>
                <CheckBox
                  isChecked={isAgreementChecked}
                  onClick={(checked) => setIsAgreementChecked(!!checked)}
                  title={
                    <>
                      {t('tours_block.confirm_offer')}{' '}
                      <span
                        className={s.offerLink}
                        onClick={(e) => {
                          e.stopPropagation()
                          void openPrivacyPolicy('tours_public_offer')
                        }}
                      >
                        {t('tours_block.offer_terms')}
                      </span>{' '}
                      {t('tours_block.and')}{' '}
                      <span
                        className={s.offerLink}
                        onClick={(e) => {
                          e.stopPropagation()
                          void openPrivacyPolicy('tours_politic_privacy_offer')
                        }}
                      >
                        {t('tours_block.privacy_policy')}
                      </span>
                    </>
                  }
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="solidBlue"
              className={clsx('semi-bold-17', s.footerButton)}
              disabled={!isAgreementChecked || isLoading}
              loading={isLoading}
              borderRadius="md"
            >
              {t('tours_block.submit_application')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
})

export default ClientDataPage
